import { useSearchParams } from 'react-router'
import { Card, CardBody, PageHeader } from '@/components/Card'
import { RefreshButton } from '@/components/RefreshButton'
import { EmptyState } from '@/components/EmptyState'
import { ErrorNotice } from '@/components/ErrorNotice'
import { RequestCard } from '@/components/RequestCard'
import { SkeletonRows } from '@/components/Skeleton'
import { StaleNotice } from '@/components/StaleNotice'
import { isStaleList, useRequests } from '@/hooks/useRequests'
import type { RequestFilter } from '@/lib/types'

const FILTERS: { value: RequestFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'downloading', label: 'Loading' },
  { value: 'available', label: 'Ready' },
]

const EMPTY_COPY: Record<RequestFilter, { title: string; description: string }> = {
  all: {
    title: 'No requests yet',
    description: 'Anything you ask for on the request site shows up here with its progress.',
  },
  pending: {
    title: 'Nothing waiting for approval',
    description: 'Requests sit here until an admin approves or declines them.',
  },
  downloading: {
    title: 'Nothing downloading',
    description: 'Approved requests show live progress here while they download.',
  },
  available: {
    title: 'Nothing ready yet',
    description: 'Finished requests collect here so you can see what is on the server.',
  },
}

function isFilter(value: string | null): value is RequestFilter {
  return value === 'all' || value === 'pending' || value === 'downloading' || value === 'available'
}

export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get('status')
  const filter: RequestFilter = isFilter(raw) ? raw : 'all'

  const requests = useRequests(filter)
  const items = requests.data?.items ?? []
  const empty = EMPTY_COPY[filter]
  const total = requests.data?.total ?? items.length

  return (
    <div className="space-y-4">
      <PageHeader
        title="Your requests"
        description="Approval, download progress and what is ready to watch."
        action={<RefreshButton />}
      />

      {/*
        Toggle buttons rather than ARIA tabs: there is one list below, not a set
        of panels, and tabs would promise arrow-key navigation we do not have.
        The strip scrolls edge to edge on a phone with no visible scrollbar.
      */}
      <div className="-mx-4 overflow-x-auto px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          role="group"
          aria-label="Filter requests"
          className="flex min-w-max divide-x divide-line rounded-[3px] border border-line bg-surface"
        >
          {FILTERS.map((option) => {
            const selected = option.value === filter
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() =>
                  setSearchParams(option.value === 'all' ? {} : { status: option.value }, {
                    replace: true,
                  })
                }
                className={`signage h-11 px-4 text-[10px] transition ${
                  selected
                    ? 'bg-accent-dim text-accent'
                    : 'text-muted hover:bg-elevated hover:text-ink'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      </div>

      {isStaleList(requests.data) ? <StaleNotice /> : null}

      <Card>
        <CardBody flush>
          {requests.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={5} />
            </div>
          ) : requests.isError && items.length === 0 ? (
            <div className="p-4">
              <ErrorNotice error={requests.error} onRetry={() => void requests.refetch()} />
            </div>
          ) : items.length === 0 ? (
            <div className="p-4">
              <EmptyState title={empty.title} description={empty.description} />
            </div>
          ) : (
            <div
              className={requests.isFetching ? 'opacity-90 transition-opacity' : undefined}
              aria-busy={requests.isFetching}
            >
              {items.map((request, index) => (
                <RequestCard key={request.id} request={request} index={index} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {items.length > 0 ? (
        <p className="flex items-baseline gap-1.5">
          <span className="font-mono text-[11px] text-faint">
            {items.length}/{total}
          </span>
          <span className="signage text-[9px] text-faint">shown</span>
        </p>
      ) : null}
    </div>
  )
}
